/**
 * Whether a failed request was rejected by a server-side rate limit.
 *
 * The anonymous generation cap (3 creates / 72h per IP) surfaces as a 429 on
 * both create endpoints. Signing in bypasses it, so callers treat this as a
 * prompt to sign in rather than as an error to show.
 *
 * Handles both axios-shaped errors (status under `response`) and the errors
 * `createResourceStream` throws for a non-ok fetch, which are given the same
 * shape deliberately so one predicate covers both create transports.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export const isRateLimited = (error) => {
  const status = error?.response?.status ?? error?.status;
  return status === 429;
};

/** Namespace the backend tags the anonymous create budget with. */
export const ANON_CREATE_NAMESPACE = 'anon_create';

/**
 * Whether a 429 means "out of free generations" specifically.
 *
 * Endpoints can carry more than one budget — /unlimited/follow-up has its own
 * per-minute burst limit *and* a non-consuming gate on the create budget, and
 * the two want opposite handling: the burst limit degrades gracefully into
 * generation, while running out of generations has to stop and ask the user to
 * sign in. The backend tags each rejection with the namespace that produced it.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export const isAnonCreateLimit = (error) =>
  isRateLimited(error) && error?.response?.data?.rate_limit?.namespace === ANON_CREATE_NAMESPACE;
